import type { ColonyEvent } from '../db/model';
import { fmt } from '../db/queries';
import { feedingSugarKg, infestationPercent, mitesPerDay, withdrawalUntil } from '../db/rules';
import {
  COLONY_STATUS,
  FEED_TYPE,
  HONEY_TYPE,
  QUEEN_CELLS,
  QUEEN_CHANGE_REASON,
  SUPER_TYPE,
  TREATMENT_METHOD,
  VARROA_METHOD,
  fmtDate,
  plural,
} from './labels';

/** Popisek z číselníku malými písmeny; neznámá hodnota (např. z ručně upravené zálohy) = prázdný text. */
const lc = (s: string | undefined) => (s ?? '').toLowerCase();

/** Jednořádkové shrnutí události pro časovou osu. */
export function describeEvent(e: ColonyEvent, names: { apiary: (id: string) => string; colony: (id: string) => string }): string {
  const parts: string[] = [];
  switch (e.type) {
    case 'inspection':
      if (e.queenSeen) parts.push('matka viděna');
      else if (e.queenSeen === false) parts.push('matka neviděna');
      if (e.eggsSeen) parts.push('vajíčka');
      else if (e.eggsSeen === false) parts.push('bez vajíček');
      if (e.occupiedSeams != null) parts.push(`${e.occupiedSeams} uliček`);
      if (e.broodFrames != null) parts.push(`plod na ${e.broodFrames} pl.`);
      if (e.storesKg != null) parts.push(`zásoby ~${fmt(e.storesKg)} kg`);
      if (e.queenCells && e.queenCells !== 'none') parts.push(`matečníky: ${lc(QUEEN_CELLS[e.queenCells])}`);
      if (e.temperament) parts.push(`mírnost ${e.temperament}/5`);
      if (e.diseaseSuspicion) parts.push('podezření na nemoc!');
      if (e.actions) parts.push(e.actions);
      break;
    case 'treatment': {
      parts.push(e.productName);
      if (e.dosePerColony != null) parts.push(`${fmt(e.dosePerColony, 2)} ${e.unit ?? ''}`.trim());
      parts.push(lc(TREATMENT_METHOD[e.method]));
      if (e.seriesIndex && e.seriesTotal) parts.push(`${e.seriesIndex}/${e.seriesTotal}`);
      if (e.batchNumber) parts.push(`šarže ${e.batchNumber}`);
      const until = withdrawalUntil(e);
      if (until) parts.push(`lhůta do ${fmtDate(until)}`);
      break;
    }
    case 'varroa_check': {
      parts.push(VARROA_METHOD[e.method]);
      if (e.method === 'winter_debris') {
        parts.push(e.miteCount != null ? `výsledek: ${e.miteCount} ${e.miteCount === 1 ? 'roztoč' : e.miteCount >= 2 && e.miteCount <= 4 ? 'roztoči' : 'roztočů'}` : 'čeká na výsledek');
        break;
      }
      const pd = mitesPerDay(e);
      const pct = infestationPercent(e);
      if (pd != null) parts.push(`spad ${e.miteCount} ${e.miteCount === 1 ? 'roztoč' : e.miteCount >= 2 && e.miteCount <= 4 ? 'roztoči' : 'roztočů'} za ${e.days} d`);
      else if (pct != null) parts.push(`${e.miteCount} / ${e.sampleSize} včel = ${fmt(pct)} %`);
      else parts.push(plural(e.miteCount, 'roztoč', 'roztoči', 'roztočů'));
      if (e.sentToLab) parts.push('odesláno k vyšetření');
      break;
    }
    case 'feeding':
      parts.push(
        `${FEED_TYPE[e.feedType] ?? 'Krmivo'}, ${fmt(e.amountPerColony)} ${e.unit ?? ''} na včelstvo` +
          ` (≈ ${fmt(feedingSugarKg(e))} kg cukru)`,
      );
      break;
    case 'harvest':
      parts.push(`${fmt(e.honeyKg)} kg, ${lc(HONEY_TYPE[e.honeyType])}`);
      if (e.colonyIds.length > 1) parts.push(`z ${e.colonyIds.length} včelstev`);
      if (e.waterPercent != null) parts.push(`voda ${fmt(e.waterPercent)} %`);
      if (e.honeyBatch) parts.push(`šarže ${e.honeyBatch}`);
      break;
    case 'queen_change':
      parts.push(e.newQueenId ? 'nová matka' : 'bez nové matky');
      if (e.reason) parts.push(lc(QUEEN_CHANGE_REASON[e.reason]));
      break;
    case 'split':
      parts.push(`vznikl oddělek ${names.colony(e.newColonyId)}`);
      if (e.framesTaken) parts.push(plural(e.framesTaken, 'plást', 'plásty', 'plástů'));
      break;
    case 'swarm':
      parts.push(e.caughtColonyId ? `roj chycen → ${names.colony(e.caughtColonyId)}` : 'roj');
      break;
    case 'move':
      parts.push(`${names.apiary(e.fromApiaryId)} → ${names.apiary(e.toApiaryId)}`);
      break;
    case 'status_change':
      parts.push(COLONY_STATUS[e.newStatus]);
      if (e.cause) parts.push(e.cause);
      break;
    case 'super_change':
      parts.push(`${e.action === 'add' ? 'nasazeno' : 'odebráno'} ${e.count}× ${lc(SUPER_TYPE[e.superType])}`);
      break;
    case 'note':
      break;
  }
  return parts.filter(Boolean).join(' · ');
}
